/** @jsxImportSource theme-ui */
import classNames from 'classnames';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Dropdown } from 'semantic-ui-react';
import { ThemeUIStyleObject } from 'theme-ui';

import theme from 'theme';
import { isOpenLinkInNewTab } from 'utils/accessibilityHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { Typography } from 'components/_common/Typography';

import { NavigationbarUrl } from '../Navigationbar';
import NavbarItem from './NavbarItem';

const Styles: ThemeUIStyleObject = {
  '&.ui.item.dropdown': {
    py: '12px',
    '& .menu': {
      bg: theme.colors.deepSea100,
      '& .item.menu-item': {
        padding: '.8125em 1.125em', // Default
      },
    },
    '& div.item.navbar_menu_item': {
      // !important because semantic ui devs decided to put it
      padding: '0 !important',
      '&.selected': { background: `${theme.colors.deepSea80} !important` },
      '&:hover': { background: `${theme.colors.deepSea80} !important` },
    },
  },
  '& .menu-icon': {
    '&.icon.bd-icon': { fontWeight: 'bold', color: 'white' },
  },
};

type NavbarMenuProps = {
  options: NavigationbarUrl;
  onClick: (url: string) => void;
};

const NavbarMenu: React.FC<NavbarMenuProps> = ({ options, onClick }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isExpanded, setIsExpanded] = useState(false);

  const handleChange = (title: string, e) => {
    const option = options.children.find(opt => opt.title === title);
    if (option.routerLink) {
      // The higher order onclick
      onClick(option.routerLink);

      isOpenLinkInNewTab(e)
        ? window.open(option.routerLink, '_blank')
        : navigate(option.routerLink);
    }
    if (option.onClick) option.onClick();
  };

  const isActive = options.children.some(opt => location.pathname.startsWith(opt.routerLink));

  return (
    <Dropdown
      className={classNames({
        'menu-item': true,
        open: isExpanded,
        active: isActive,
      })}
      direction={options.direction}
      onOpen={() => setIsExpanded(true)}
      onClose={() => setIsExpanded(false)}
      active={isActive ? true : undefined} // handles console error
      item
      aria-label={options.title}
      trigger={
        options.icon != null ? (
          <BdIcon name={options.icon} color="white" className="display circle-mask" />
        ) : (
          <Typography variant="body2" className="display" marginBottom="0px">
            {options.title}
          </Typography>
        )
      }
      icon={
        <BdIcon
          name={isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'}
          className="display menu-icon"
        />
      }
      onChange={(e, d) => {
        handleChange(d.value as string, e);
      }}
      selectOnNavigation={false}
      selectOnBlur={false}
      openOnFocus={false}
      value={null}
      options={options.children.map(link => ({
        value: link.title,
        className: 'navbar_menu_item',
        content: <NavbarItem options={link} />,
      }))}
      sx={Styles}
    />
  );
};

export default NavbarMenu;
